import useAutosaveController from 'hooks/useAutosaveController';
import useFormValidation from 'hooks/useFormValidation';
import { isEqual } from 'lodash';
import { useCallback, useMemo, useState } from 'react';

export const SETTINGS_AUTOSAVE_DELAY = 1500;
const identity = (value) => value;
const identityField = (_, value) => value;

const useAutoSaveForm = ({
    fields,
    getErrorMessage,
    initialValues,
    normalize = identity,
    normalizeField = identityField,
    onError,
    onSuccess,
    persist,
    debounceMs = SETTINGS_AUTOSAVE_DELAY,
    enabled = true,
}) => {
    const [form, setForm] = useState(initialValues);
    const { errors, validate, validateAll } = useFormValidation(form, getErrorMessage);
    const knownFieldNames = useMemo(() => (fields ? Object.values(fields) : null), [fields]);
    const controller = useAutosaveController({
        value: form,
        enabled,
        debounceMs,
        normalize,
        persist,
        validate: (values) => Object.entries(values).every(([key, value]) => !getErrorMessage(key, value, values)),
        onInvalid: (values) => validateAll(values),
        onSuccess,
        onError,
    });
    const { flush, reset: controllerReset } = controller;

    const assertKnownField = useCallback(
        (key) => {
            const isKnownField = knownFieldNames
                ? knownFieldNames.includes(key)
                : Object.prototype.hasOwnProperty.call(form, key);
            if (!isKnownField) throw new Error(`Unknown auto-save form field: ${String(key)}`);
        },
        [form, knownFieldNames]
    );

    const updateField = useCallback(
        (key, value) => {
            assertKnownField(key);
            const normalizedValue = normalizeField(key, value);
            setForm((current) => {
                if (isEqual(current[key], normalizedValue)) return current;
                const next = { ...current, [key]: normalizedValue };
                if (errors[key]) validate(key, value, next);
                return next;
            });
        },
        [assertKnownField, errors, normalizeField, validate]
    );

    const updateAndSave = useCallback(
        (key, value) => {
            assertKnownField(key);
            const normalizedValue = normalizeField(key, value);
            const next = { ...form, [key]: normalizedValue };
            setForm(next);
            return flush({ reason: 'immediate', nextValue: next });
        },
        [assertKnownField, flush, form, normalizeField]
    );

    const saveField = useCallback(
        (key) => {
            assertKnownField(key);
            return flush({ reason: 'blur' });
        },
        [assertKnownField, flush]
    );

    const replace = useCallback((next) => setForm(next), []);
    const replaceAndSave = useCallback(
        (next) => {
            setForm(next);
            return flush({ reason: 'immediate', nextValue: next });
        },
        [flush]
    );
    const reset = useCallback(
        (next, options) => {
            setForm(next);
            controllerReset(next, options);
        },
        [controllerReset]
    );

    return {
        ...controller,
        errors,
        form,
        replace,
        replaceAndSave,
        reset,
        saveField,
        setForm,
        updateAndSave,
        updateField,
    };
};

export default useAutoSaveForm;
